import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import ConfirmModal from "../../components/admin/ConfirmModal";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import { orgAdminService } from "../../features/org-admin/service";
import type { OrgCandidateUser, OrganizationSummary } from "../../features/org-admin/types";
import { formatISTDateTime } from "../../utils/dateUtils";

const OrgAdminMembersPage: React.FC = () => {
  const [members, setMembers] = useState<OrgCandidateUser[] | null>(null);
  const [org, setOrg] = useState<OrganizationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [removeTarget, setRemoveTarget] = useState<OrgCandidateUser | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const { alertModal, showAlert } = useAlertModal();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, organization] = await Promise.all([
        orgAdminService.getMembers(),
        orgAdminService.getOrganization(),
      ]);
      setMembers(rows);
      setOrg(organization);
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to load your members.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRemove = async (user: OrgCandidateUser) => {
    setRemoveBusy(true);
    try {
      await orgAdminService.removeMember(user.user_id);
      setRemoveTarget(null);
      await fetchAll();
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to remove this member.");
    } finally {
      setRemoveBusy(false);
    }
  };

  return (
    <>
      <AdminPageHeader
        title="Members"
        subtitle="Everyone who has accepted and joined. Removing someone frees a seat — their personal Gullivr account, deals and wallet are untouched."
        meta={org ? `${org.active_member_count}/${org.seat_limit} seats used` : undefined}
        actions={<RefreshButton onRefresh={fetchAll} />}
      />

      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Member</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Role in org</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Account type</th>
                <th className="text-left py-3 px-4 text-gray-500 font-medium">Joined</th>
                <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
              ) : !members || members.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-20 text-center text-gray-500">
                    0 members so far
                    {org && org.candidate_count > 0 ? (
                      <>
                        , {org.candidate_count} {org.candidate_count === 1 ? "person" : "people"} you can invite.{" "}
                        <Link to="/org-admin/candidates" className="text-indigo-400 hover:text-indigo-300">
                          Go to candidates
                        </Link>
                        .
                      </>
                    ) : (
                      "."
                    )}
                  </td>
                </tr>
              ) : (
                members.map((user) => {
                  const isOrgAdmin = user.organization?.role === "org_admin";
                  return (
                    <tr key={user.user_id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="text-gray-200">{user.name || "Unnamed user"}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                          isOrgAdmin ? "bg-indigo-500/10 text-indigo-400" : "bg-gray-500/10 text-gray-400"
                        }`}>
                          {isOrgAdmin ? "Org admin" : "Member"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-400 text-xs">{user.role || "—"}</td>
                      <td className="py-3 px-4 text-gray-500 text-xs">{formatISTDateTime(user.organization?.joined_at)}</td>
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        {isOrgAdmin ? (
                          // The sole org admin can't be removed here — that's a
                          // Gullivr-side reassignment, per the backend's own rule.
                          <span className="text-xs text-gray-600">—</span>
                        ) : (
                          <button
                            onClick={() => setRemoveTarget(user)}
                            className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {removeTarget && (
        <ConfirmModal
          title="Remove member"
          description={`${removeTarget.name || removeTarget.email} will leave ${org?.name || "your organization"} and a seat frees up. Their personal Gullivr account is not affected.`}
          items={["They lose the organization workspace switch", "They return to your candidate pool only if Gullivr re-adds them"]}
          confirmLabel="Remove"
          busy={removeBusy}
          busyLabel="Removing…"
          onConfirm={() => handleRemove(removeTarget)}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
      {alertModal}
    </>
  );
};

export default OrgAdminMembersPage;
